/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { ProcessedMetricItem } from 'cio/utils/metrics/analysis';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { generateSortBy, SortDirection } from 'cio/utils/sort';
import { Channel } from 'cio/utils/metrics/common';
import { CampaignType } from 'cio/models/campaign';
import { MetricItem } from 'cio/utils/metrics/analysis';
import { inject as service } from '@ember/service';
import HydraService from 'cio/services/hydra';

interface AnalysisPageTableArgs {
  sortBy: string;
  sortDirection: SortDirection;
  onSort: (sortBy: string, direction: SortDirection) => unknown;
  selectedMeasures: string[];
  onSelectedMeasuresChanged: (selectedMeasures: string[]) => unknown;
  selectedChannels: string[];
  refinedCampaignAndNewsletterIds: string[];
  campaignAndNewsletterCount: number;
  isLoading: boolean;
  totalMetric: {
    delivery_metrics: Record<string, number | number[]>;
    journey_metrics: Record<string, number | number[]>;
    per_channel_metrics: Record<Channel, Record<string, number | number[]>>;
    per_subscription_topic_metrics: Record<string, Record<string, number>>;
  } | null;
  rawSummedMetrics: ProcessedMetricItem[] | null;
  rangeStartBeforeJourneyMetricsAvailability: boolean;
  metrics: MetricItem[];
}

function generateSortByRate(sortBy: string, metricAccessor: string, sortDirection: string) {
  return function (a: any, b: any) {
    let valueA = a.data[metricAccessor][`${sortBy}_rate`];
    let valueB = b.data[metricAccessor][`${sortBy}_rate`];

    let sortDirectionValue = sortDirection === SortDirection.Ascending ? 1 : -1;

    // If one of the values is null, sort the non-null value higher (even if it
    // is 0)
    if (valueA != null && valueB == null) {
      return sortDirectionValue;
    }

    if (valueB != null && valueA == null) {
      return -sortDirectionValue;
    }

    // Fall back to sort by total
    if (valueA == null && valueB == null) {
      valueA = a.data[metricAccessor][`total_${sortBy}`];
      valueB = b.data[metricAccessor][`total_${sortBy}`];
    }

    return (valueA - valueB) * sortDirectionValue;
  };
}

const DEFAULT_VERTICAL_COLLECTION_CONTAINER_SELECTOR = 'body';

export default class AnalysisPageTable extends Component<AnalysisPageTableArgs> {
  @service hydra!: HydraService;

  @tracked sortedSummedMetricsData: ProcessedMetricItem[] | null = null;

  get totalTypes() {
    let itemTypes: Partial<Record<CampaignType | 'newsletter' | 'transactional_message', boolean>> = {};
    let newsletterChannels: Partial<Record<Channel, boolean>> = {};
    let transactionalChannels: Partial<Record<Channel, boolean>> = {};
    let anyConversions = false;

    (this.sortedSummedMetricsData ?? []).forEach((m) => {
      itemTypes[m.type] = true;

      if (m.type === 'newsletter' && m.channel != null) {
        newsletterChannels[m.channel as Channel] = true;
      }

      if (m.type === 'transactional_message' && m.channel != null) {
        transactionalChannels[m.channel as Channel] = true;
      }

      if (!anyConversions && m.conversion) {
        anyConversions = true;
      }
    });

    return {
      itemTypes: Object.keys(itemTypes),
      newsletterChannels: Object.keys(newsletterChannels),
      transactionalChannels: Object.keys(transactionalChannels),
      anyConversions,
    };
  }

  get sortedSelectedMetrics() {
    let selectedMetrics = this.args.selectedMeasures;

    return this.args.metrics.reduce(
      (acc, group) => {
        let selectedGroupItems = group.items.filter((item) => selectedMetrics.includes(item.value));
        return [...acc, ...selectedGroupItems];
      },
      [] as { label: string; measureType: string; measureName: string; value: string }[],
    );
  }

  get totalRowLabel() {
    return `${this.args.refinedCampaignAndNewsletterIds.length ?? 0} of ${
      this.args.campaignAndNewsletterCount ?? 0
    } results (total)`;
  }

  get verticalCollectionContainerSelector() {
    if (this.hydra.isHydraEnabled) {
      return this.hydra.config?.scrollContainerSelector ?? DEFAULT_VERTICAL_COLLECTION_CONTAINER_SELECTOR;
    }

    return DEFAULT_VERTICAL_COLLECTION_CONTAINER_SELECTOR;
  }

  @action
  sortMetrics() {
    let metrics = this.args.rawSummedMetrics ?? [];

    let metricAccessor = '';
    let { sortBy } = this.args;

    if (this.args.sortBy.includes('.')) {
      let [metricType, measure] = this.args.sortBy.split('.');
      sortBy = measure;
      metricAccessor = metricType;
    }
    let sortFunction;

    if (sortBy === 'campaign_name') {
      sortFunction = generateSortBy(this.args.sortDirection, 'name');
    } else if (sortBy === 'sent') {
      sortFunction = generateSortBy(this.args.sortDirection, 'data.delivery_metrics.total_sent');
    } else if (sortBy === 'activated') {
      sortFunction = generateSortBy(this.args.sortDirection, 'data.journey_metrics.total_activated');
    } else {
      sortFunction = generateSortByRate(sortBy, metricAccessor, this.args.sortDirection);
    }

    let sortedMetrics = [...metrics].sort(sortFunction);

    this.sortedSummedMetricsData = sortedMetrics.filter((d: any) =>
      this.args.refinedCampaignAndNewsletterIds.includes(String(d.id)),
    );
  }

  @action
  onSort(sortBy: string, direction: SortDirection) {
    this.args.onSort(sortBy, direction);

    this.sortMetrics();
  }
}
